import "./Feedback.scss";

import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import AbunTable from "components/AbunTable/AbunTable";
import { DateTime } from "luxon";

export interface ArticleTitle {
    articleUID: string
    articleTitle: string
    createdOn: string
    userEmail: string
    userId: string
    keyword: string
    keywordTraffic: number | null
    internalLinks: number | null
    externalLinks: number | null
    images: number | null
    wordCount: number | null
    isProcessing: boolean
    isGenerated: boolean
    isPosted: boolean
    isFailed: boolean
    isArchived: boolean
    postLink: string
    isUserAdded: boolean
    postedTo: string
    feedback: string
    postedOn: string
}

export default function Feedbacks() {
    // ---------------------- NON STATE CONSTANTS ----------------------
    const pageSizes = [10, 50, 150, 250, 500];

    const [selectedFeedBack, setSelectedFeedBack] = useState("");
    // ---------------------- TABLE COLUMN DEFS ----------------------
    const columnHelper = createColumnHelper<ArticleTitle>();
    const columnDefs: ColumnDef<any, any>[] = [
        columnHelper.accessor((row: ArticleTitle) => row.articleTitle, {
            id: 'title',
            header: "Article Title",
            cell: props => {
                if (props.row.original.isProcessing || props.row.original.isGenerated) {
                    return (
                        <NavLink to={`/view-article/${props.row.original.articleUID}`} >
                            {props.row.original.articleTitle}
                        </NavLink>
                    )
                } else {
                    return props.row.original.articleTitle;
                }
            },
            enableGlobalFilter: true,
            enableSorting: true
        }),
        columnHelper.accessor((row: ArticleTitle) => row.articleUID, {
            id: 'article_uid',
            header: "Article UID",
            cell: info => info.getValue(),
            enableGlobalFilter: true,
            enableSorting: true
        }),
        columnHelper.accessor((row: ArticleTitle) => row.userEmail, {
            id: 'email_id',
            header: "Email ID",
            cell: props => {
                return (
                    <Link to={`/all-users/${props.row.original.userId}`}>
                        <span>{props.row.original.userEmail}</span>
                    </Link>
                )
            },
            enableGlobalFilter: true,
        }),
        columnHelper.accessor((row: ArticleTitle) => row.keyword, {
            id: 'keyword',
            header: "Keyword [Traffic]",
            cell: props => {
                return (
                    `${props.row.original.keyword} [${props.row.original.keywordTraffic}]`
                )
            },
            enableGlobalFilter: true,
            enableSorting: true
        }),
        columnHelper.accessor((row: ArticleTitle) => row.wordCount, {
            id: 'word_cound',
            header: "Word Count",
            cell: info => {
                if (info.getValue()) {
                    return info.getValue()
                } else {
                    return "---"
                }
            },
            enableGlobalFilter: false,
            enableSorting: true
        }),

        columnHelper.accessor((row: ArticleTitle) => row.createdOn, {
            id: 'created_on',
            header: "Created On",
            cell: info => info.getValue(),
            enableGlobalFilter: false,
        }),

        columnHelper.display({
            id: 'posted_to',
            header: "Posted To",
            cell: props => {
                if (props.row.original.postedTo && props.row.original.postLink) {
                    return props.row.original.postedTo
                } else {
                    return "--"
                }
            },
            enableGlobalFilter: true,
        }),
        columnHelper.accessor((row: ArticleTitle) => row.feedback, {
            id: 'feedback',
            header: "Feedback",
            cell: info => {
                // only show feedback if article is generated and feedback is not "no_feedback"
                if (info.row.original.isGenerated && info.getValue() !== "no_feedback") {
                    // capitalize first letter
                    return info.getValue().charAt(0).toUpperCase() + info.getValue().slice(1);
                } else {
                    return "---"
                }
            },
            enableGlobalFilter: false,
        }),
        columnHelper.display({
            id: 'article_link',
            header: "Article Link",
            cell: props => {
                if (props.row.original.postLink) {
                    return (
                        <Link target="_blank" to={props.row.original.postLink} className={"button is-primary"}>
                            View
                        </Link>
                    )
                } else {
                    return (
                        <button style={{ backgroundColor: "#86a5ec", cursor: "not-allowed" }} className="button is-primary">
                            View
                        </button>
                    )
                }
            },
            enableGlobalFilter: false,
        }),
    ]

    // ==============================================================
    // ---------------------- MAIN RENDER CODE ----------------------
    // ==============================================================
    return (
        <div className={"card w-100 mt-4"}>
            <div className={"card-content"}>
                <div className={"content"}>

                    <div className={"all-users-container"}>
                        <div className={"table-controls table-controls-header"}>
                            <div className={"table-custom-controls"}>
                                <div className={"select table-custom-control--item"}>
                                    <select value={selectedFeedBack} onChange={e => setSelectedFeedBack(e.target.value)}>
                                        <option value="all">All Feedback</option>
                                        <option value="positive">Positive</option>
                                        <option value="negative">Negative</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <AbunTable tableContentName={"Users"}
                            serverSide={true}
                            apiUrl="/api/admin/get-all-articles-with-feedback/"
                            searchboxPlaceholderText={"Search for Articles..."}
                            seletedFeedBack={selectedFeedBack}
                            columnDefs={columnDefs}
                            pageSizes={pageSizes}
                            initialPageSize={pageSizes[0]}
                            noDataText={"No Articles Found"}
                            enableSorting={true}
                            transformResponse={(rawData) => {
                                const articles = rawData.articles || [];
                                let filteredData = articles;

                                if (selectedFeedBack && selectedFeedBack !== "all") {
                                    filteredData = articles.filter((article: any) => article.feedback === selectedFeedBack);
                                }

                                return {
                                    data: filteredData.map((article: ArticleTitle) => ({
                                        articleUID: article.articleUID,
                                        articleTitle: article.articleTitle,
                                        createdOn: article.createdOn,
                                        userEmail: article.userEmail,
                                        userId: article.userId,
                                        keyword: article.keyword,
                                        keywordTraffic: article.keywordTraffic,
                                        wordCount: article.wordCount,
                                        isGenerated: article.isGenerated,
                                        isProcessing: article.isProcessing,
                                        isArchived: article.isArchived,
                                        postedTo: article.postedTo,
                                        feedback: article.feedback,
                                        postLink: article.postLink,
                                        postedOn: article.postedOn
                                    })),
                                    total: rawData.total,
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
